/**
 * Generic authenticated mutation hook.
 * Wraps TanStack Query's useMutation with Clerk auth token extraction
 * and optional cache invalidation.
 *
 * Usage:
 *   useApiMutation({
 *     mutationFn: (token, data) => api.create(token, data),
 *     invalidateKeys: [['items']],
 *   })
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryKey, UseMutationResult } from '@tanstack/react-query';
import { useAuth } from '../lib/auth';
import { log } from '@/lib/logger';

log.info('[useApiMutation] Module loaded');

export interface UseApiMutationOptions<TData, TVariables, TContext = unknown> {
  /** Receives auth token (guaranteed non-null) and variables */
  mutationFn: (token: string, variables: TVariables) => Promise<TData>;
  /** Query keys to invalidate on success */
  // readonly so shared `as const` key sets (ISSUE_MUTATION_INVALIDATE_KEYS)
  // can be passed directly. Strictly more permissive: this is only iterated.
  invalidateKeys?: readonly QueryKey[];
  /** Runs after invalidation is requested; receives onMutate context for partial rollback. */
  onSuccess?: (data: TData, variables: TVariables, context: TContext | undefined) => void;
  /**
   * Custom onError handler. Receives the context onMutate returned, so an
   * optimistic write (e.g. a row removed from a cached list) can be rolled
   * back here instead of surviving a refused mutation until the next manual
   * refresh (nightly review 2026-08-17: useMoveIssues removed rows in onMutate
   * but nothing ever put them back).
   */
  onError?: (error: Error, variables: TVariables, context: TContext | undefined) => void;
  /** Optimistic update — runs before mutationFn. Return context for rollback in onError. */
  onMutate?: (variables: TVariables) => Promise<TContext> | TContext;
  /** Runs on error with the context returned by onMutate (for rollback). */
  onSettled?: () => void;
}

export function useApiMutation<TData, TVariables, TContext = unknown>(
  options: UseApiMutationOptions<TData, TVariables, TContext>,
): UseMutationResult<TData, Error, TVariables, TContext> {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  log.info('[useApiMutation] Hook called');

  return useMutation<TData, Error, TVariables, TContext>({
    mutationFn: async (variables: TVariables) => {
      log.info('[useApiMutation] mutationFn executing');
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      return options.mutationFn(token, variables);
    },
    onMutate: options.onMutate,
    onSuccess: (data, variables, context) => {
      log.info('[useApiMutation] onSuccess, invalidating keys:', { invalidateKeys: options.invalidateKeys });
      if (options.invalidateKeys) {
        for (const key of options.invalidateKeys) {
          queryClient.invalidateQueries({ queryKey: key });
        }
      }
      options.onSuccess?.(data, variables, context);
    },
    onError: options.onError
      ? (error, variables, context) => {
          log.info('[useApiMutation] onError:', { message: error.message });
          options.onError!(error, variables, context);
        }
      : undefined,
    onSettled: options.onSettled,
  });
}
